import React from 'react';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { ImageEditorWithCrop } from '../shared/ImageThumbnailWithCrop';
import { lockedSlotAspect } from '../shared/cropUtils';
import type { CropData } from '../shared/ImageCropModal';
import type { Area } from 'react-easy-crop';

export interface GalleryItem {
  id: string;
  imageUrl: string;
  cloudfrontUrl?: string;
  alt?: string;
  caption?: string;
  category?: string;
  order: number;
  crop?: CropData;
}

interface ImageGalleryItemCardProps {
  item: GalleryItem;
  index: number;
  totalItems: number;
  filtering: boolean;
  aspectRatio?: string;
  onUpdate: (field: string, value: any) => void;
  onUpdateFields?: (fields: Record<string, any>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onUpload: () => void;
}

export const ImageGalleryItemCard: React.FC<ImageGalleryItemCardProps> = ({
  item,
  index,
  totalItems,
  filtering,
  aspectRatio,
  onUpdate,
  onUpdateFields,
  onRemove,
  onMove,
  onUpload,
}) => {
  const slotAspect = lockedSlotAspect(aspectRatio);

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm font-medium text-gray-500">Image {index + 1}</span>
        <div className="flex items-center space-x-1">
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={index === totalItems - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ChevronDown size={16} />
          </button>
          <button type="button" onClick={onRemove} className="p-1 text-red-400 hover:text-red-600">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {/* Image with Crop */}
        <ImageEditorWithCrop
          imageUrl={item.imageUrl}
          cloudfrontUrl={item.cloudfrontUrl}
          crop={item.crop}
          onUrlChange={url => onUpdate('imageUrl', url)}
          onUpload={onUpload}
          onRemove={() => {
            if (onUpdateFields) {
              onUpdateFields({ imageUrl: '', cloudfrontUrl: '', crop: undefined });
            } else {
              onUpdate('imageUrl', '');
              onUpdate('cloudfrontUrl', '');
              onUpdate('crop', {} as CropData);
            }
          }}
          onCropChange={(crop: CropData, _croppedAreaPixels: Area) => {
            onUpdate('crop', crop);
          }}
          label="Image"
          required
          cropMode={slotAspect ? 'frame' : 'aspect'}
          lockedAspect={slotAspect}
        />

        {/* Alt Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
          <input
            type="text"
            value={item.alt || ''}
            onChange={e => onUpdate('alt', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Descriptive text for accessibility"
            maxLength={200}
          />
        </div>

        {/* Caption */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Caption</label>
          <textarea
            value={item.caption || ''}
            onChange={e => onUpdate('caption', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            rows={2}
            placeholder="Optional caption text"
            maxLength={500}
          />
        </div>

        {/* Category */}
        {filtering && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <input
              type="text"
              value={item.category || ''}
              onChange={e => onUpdate('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Nature, Urban, Events"
              maxLength={100}
            />
          </div>
        )}
      </div>
    </div>
  );
};
